import { Suspense, type ReactNode } from "react"

import { ErrorBoundary } from "@/components/common/ErrorBoundary"
import { Typography } from "@/components/common/Typography/Typography"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"

/** The panel an Excel preview shows while it loads, or when it could not. */
export function ExcelPreviewState({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="flex h-full items-center justify-center bg-grey3">
      <div className="text-center">
        <Typography as="h2" variant="titleM" className="pb-M">
          {title}
        </Typography>
        <Typography
          as="p"
          variant="textS"
          className={action ? "pb-L text-grey5" : "text-grey5"}
        >
          {description}
        </Typography>
        {action}
      </div>
    </div>
  )
}

/**
 * Around the lazily loaded editor: its code failing to download (a deploy
 * replaced the chunk, the network dropped) shows the preview's error panel,
 * not the app's error page. Reloading the page is the retry: a lazy component
 * whose import failed stays failed.
 */
export function ExcelEditorBoundary({ children }: { children: ReactNode }) {
  const { translate } = useTranslation()
  return (
    <ErrorBoundary
      fallback={
        <ExcelPreviewState
          title={translate("ERROR/SOMETHING_WENT_WRONG")}
          description={translate("ERROR/EXCEL_LOAD_FAILED")}
          action={
            <Button variant="outline" onClick={() => window.location.reload()}>
              {translate("QUESTIONNAIRE_ANSWERS/RELOAD")}
            </Button>
          }
        />
      }
    >
      <Suspense fallback={<ExcelPreviewLoading />}>{children}</Suspense>
    </ErrorBoundary>
  )
}

/** The panel while a preview loads, worded as the old viewer's. */
export function ExcelPreviewLoading() {
  const { translate } = useTranslation()
  return (
    <ExcelPreviewState
      title={translate("QUESTIONNAIRE_ANSWERS/PREVIEW_TITLE")}
      description={translate("QUESTIONNAIRE_ANSWERS/PREVIEW_DESCRIPTION")}
    />
  )
}
